import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { SafetyTestService } from '../../core/services/safety-test.service';
import { SafetyTestCategoryService } from '../../core/services/safety-test-category.service';
import {
  SafetyTestWithQuestions,
  SafetyTestCategory,
  TestQuestion
} from '../../core/models';

declare var bootstrap: any;

interface QuestionForm {
  id?: string;
  text: string;
  explanation: string;
  type: 'multiple_choice' | 'true_false';
  options: OptionForm[];
  expanded: boolean;
}

interface OptionForm {
  id?: string;
  text: string;
  isCorrect: boolean;
}

@Component({
  selector: 'app-safety-test-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './safety-test-form.component.html',
  styleUrl: './safety-test-form.component.scss'
})
export class SafetyTestFormComponent implements OnInit {
  testId: string | null = null;
  isEditMode = false;
  loading = true;
  saving = false;
  errorMessage = '';
  successMessage = '';

  categories: SafetyTestCategory[] = [];

  // Form data
  form: FormGroup;
  questions: QuestionForm[] = [];

  // Drag state
  draggedQuestionIndex: number | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private safetyTestService: SafetyTestService,
    private categoryService: SafetyTestCategoryService
  ) {
    this.form = this.fb.group({
      title: ['', Validators.required],
      shortName: [''],
      description: [''],
      categoryId: [''],
      passingScore: [70, [Validators.required, Validators.min(1), Validators.max(100)]],
      timeLimit: [null],
      shuffleQuestions: [false],
      shuffleOptions: [false],
      versionNotes: ['']
    });
  }

  ngOnInit(): void {
    this.testId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.testId;

    this.loadCategories();

    if (this.isEditMode) {
      this.loadTestDetail();
    } else {
      this.loading = false;
      // Add one empty question for new tests
      this.addQuestion();
    }
  }

  async loadCategories(): Promise<void> {
    try {
      this.categories = await this.categoryService.listCategories({ systemOnly: true });
    } catch (error: any) {
      console.error('Failed to load categories:', error);
    }
  }

  async loadTestDetail(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const test = await this.safetyTestService.getTest(this.testId!);

      if (!test) {
        this.errorMessage = 'Test not found';
        return;
      }

      // Populate form
      this.form.patchValue({
        title: test.title,
        shortName: test.shortName || '',
        description: test.description || '',
        categoryId: test.categoryId || '',
        passingScore: test.passingScore,
        timeLimit: test.timeLimit || null,
        shuffleQuestions: test.shuffleQuestions,
        shuffleOptions: test.shuffleOptions,
        versionNotes: test.versionNotes || ''
      });

      // Populate questions
      this.questions = (test.questions || []).map(q => ({
        id: q.id,
        text: q.text,
        explanation: q.explanation || '',
        type: q.type || 'multiple_choice',
        options: q.options.map(o => ({
          id: o.id,
          text: o.text,
          isCorrect: o.isCorrect
        })),
        expanded: false
      }));

      // Disable structural fields if test is active
      if (test.status === 'active') {
        this.form.get('passingScore')?.disable();
        this.form.get('timeLimit')?.disable();
      }
    } catch (error: any) {
      console.error('Failed to load test:', error);
      this.errorMessage = 'Failed to load test details';
    } finally {
      this.loading = false;
    }
  }

  // Question Management
  addQuestion(): void {
    this.questions.push({
      text: '',
      explanation: '',
      type: 'multiple_choice',
      options: [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false }
      ],
      expanded: true
    });
    // Collapse other questions
    this.questions.forEach((q, i) => {
      if (i < this.questions.length - 1) q.expanded = false;
    });
  }

  removeQuestion(index: number): void {
    this.questions.splice(index, 1);
  }

  toggleQuestion(index: number): void {
    this.questions[index].expanded = !this.questions[index].expanded;
  }

  duplicateQuestion(index: number): void {
    const original = this.questions[index];
    const duplicate: QuestionForm = {
      text: original.text + ' (copy)',
      explanation: original.explanation,
      type: original.type,
      options: original.options.map(o => ({ text: o.text, isCorrect: o.isCorrect })),
      expanded: true
    };
    this.questions.splice(index + 1, 0, duplicate);
  }

  moveQuestionUp(index: number): void {
    if (index > 0) {
      [this.questions[index], this.questions[index - 1]] =
        [this.questions[index - 1], this.questions[index]];
    }
  }

  moveQuestionDown(index: number): void {
    if (index < this.questions.length - 1) {
      [this.questions[index], this.questions[index + 1]] =
        [this.questions[index + 1], this.questions[index]];
    }
  }

  // Question Type Change
  onQuestionTypeChange(question: QuestionForm): void {
    if (question.type === 'true_false') {
      question.options = [
        { text: 'True', isCorrect: true },
        { text: 'False', isCorrect: false }
      ];
    } else if (question.options.length < 2) {
      question.options = [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false }
      ];
    }
  }

  // Option Management
  addOption(question: QuestionForm): void {
    if (question.type !== 'true_false' && question.options.length < 6) {
      question.options.push({ text: '', isCorrect: false });
    }
  }

  removeOption(question: QuestionForm, optionIndex: number): void {
    if (question.options.length > 2) {
      const wasCorrect = question.options[optionIndex].isCorrect;
      question.options.splice(optionIndex, 1);
      // If we removed the correct answer, set first option as correct
      if (wasCorrect && question.options.length > 0) {
        question.options[0].isCorrect = true;
      }
    }
  }

  setCorrectOption(question: QuestionForm, optionIndex: number): void {
    question.options.forEach((o, i) => o.isCorrect = i === optionIndex);
  }

  // Drag and Drop
  onDragStart(index: number): void {
    this.draggedQuestionIndex = index;
  }

  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent, targetIndex: number): void {
    event.preventDefault();
    if (this.draggedQuestionIndex !== null && this.draggedQuestionIndex !== targetIndex) {
      const draggedQuestion = this.questions[this.draggedQuestionIndex];
      this.questions.splice(this.draggedQuestionIndex, 1);
      this.questions.splice(targetIndex, 0, draggedQuestion);
    }
    this.draggedQuestionIndex = null;
  }

  onDragEnd(): void {
    this.draggedQuestionIndex = null;
  }

  // Validation
  isQuestionValid(question: QuestionForm): boolean {
    if (!question.text.trim()) return false;
    if (question.options.length < 2) return false;
    const hasCorrect = question.options.some(o => o.isCorrect);
    const allHaveText = question.options.every(o => o.text.trim());
    return hasCorrect && allHaveText;
  }

  get allQuestionsValid(): boolean {
    return this.questions.length > 0 && this.questions.every(q => this.isQuestionValid(q));
  }

  get canSave(): boolean {
    return this.form.valid;
  }

  get canActivate(): boolean {
    return this.form.valid && this.allQuestionsValid;
  }

  // Save
  async save(activate = false): Promise<void> {
    if (!this.canSave) return;
    if (activate && !this.canActivate) return;

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const formValue = this.form.getRawValue();

      const testData = {
        title: formValue.title,
        shortName: formValue.shortName || undefined,
        description: formValue.description || undefined,
        categoryId: formValue.categoryId || undefined,
        passingScore: formValue.passingScore,
        timeLimit: formValue.timeLimit || undefined,
        shuffleQuestions: formValue.shuffleQuestions,
        shuffleOptions: formValue.shuffleOptions,
        versionNotes: formValue.versionNotes || undefined
      };

      const questionsData = this.questions.map(q => ({
        text: q.text,
        explanation: q.explanation || undefined,
        type: q.type,
        options: q.options.map(o => ({
          text: o.text,
          isCorrect: o.isCorrect
        }))
      }));

      let test;

      if (this.isEditMode) {
        // Update existing test
        test = await this.safetyTestService.updateTest(this.testId!, testData);

        // Note: For simplicity, we're not handling individual question updates here
        // In production, you'd want to diff and update/add/remove questions
      } else {
        // Create new test with questions
        test = await this.safetyTestService.createTest(testData, questionsData);
      }

      if (!test) {
        this.errorMessage = 'Failed to save test';
        return;
      }

      // Activate if requested
      if (activate && test.id) {
        const activated = await this.safetyTestService.activateTest(test.id);
        if (!activated) {
          this.errorMessage = 'Test saved but activation failed';
          return;
        }
      }

      this.successMessage = activate ? 'Test saved and activated!' : 'Test saved!';

      // Navigate to detail page
      setTimeout(() => {
        this.router.navigate(['/safety-tests', test!.id, 'details']);
      }, 1000);
    } catch (error: any) {
      console.error('Failed to save test:', error);
      this.errorMessage = error.message || 'Failed to save test';
    } finally {
      this.saving = false;
    }
  }

  goBack(): void {
    if (this.isEditMode) {
      this.router.navigate(['/safety-tests', this.testId, 'details']);
    } else {
      this.router.navigate(['/safety-tests']);
    }
  }

  // CSV Import
  private importModal: any;
  importPreview: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    questions: {
      text: string;
      type: string;
      explanation: string | null;
      options: { text: string; isCorrect: boolean }[];
      isValid: boolean;
      errors: string[];
    }[];
  } | null = null;
  importCsvData = '';
  importing = false;
  importReplaceMode = false;

  openImportModal(): void {
    this.importCsvData = '';
    this.importPreview = null;
    this.importReplaceMode = this.isEditMode && this.questions.length > 0;
    const modalElement = document.getElementById('importModal');
    if (modalElement) {
      this.importModal = new bootstrap.Modal(modalElement);
      this.importModal.show();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        this.importCsvData = e.target?.result as string;
        this.previewImport();
      };
      reader.readAsText(file);
    }
  }

  async previewImport(): Promise<void> {
    if (!this.importCsvData.trim()) {
      this.importPreview = null;
      return;
    }

    // For new tests (not yet saved), parse locally
    if (!this.isEditMode) {
      this.parseLocalCsv();
      return;
    }

    try {
      this.importPreview = await this.safetyTestService.previewImportQuestions(
        this.testId!,
        this.importCsvData
      );
    } catch (error: any) {
      console.error('Failed to preview import:', error);
      this.errorMessage = 'Failed to preview CSV';
    }
  }

  private parseLocalCsv(): void {
    const lines = this.importCsvData.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      this.importPreview = null;
      return;
    }

    const dataLines = lines.slice(1);
    const questions: any[] = [];

    for (let i = 0; i < dataLines.length; i++) {
      const fields = this.parseCsvLine(dataLines[i]);
      const errors: string[] = [];

      // Extract question data - handle both with and without question number
      let questionText: string;
      let questionType: string;
      let explanation: string | null;
      let optionStartIndex: number;

      if (!isNaN(parseInt(fields[0]))) {
        // Has question number
        questionText = fields[1]?.trim() || '';
        questionType = fields[2]?.trim() || 'multiple_choice';
        explanation = fields[3]?.trim() || null;
        optionStartIndex = 4;
      } else {
        // No question number
        questionText = fields[0]?.trim() || '';
        questionType = fields[1]?.trim() || 'multiple_choice';
        explanation = fields[2]?.trim() || null;
        optionStartIndex = 3;
      }

      if (!questionText) {
        errors.push(`Line ${i + 2}: Question text is required`);
      }

      const options: { text: string; isCorrect: boolean }[] = [];

      for (let j = optionStartIndex; j < fields.length; j += 2) {
        const optionText = fields[j]?.trim();
        const optionCorrect = fields[j + 1]?.trim()?.toUpperCase();

        if (optionText) {
          options.push({
            text: optionText,
            isCorrect: optionCorrect === 'TRUE' || optionCorrect === 'YES' || optionCorrect === '1'
          });
        }
      }

      if (options.length < 2) {
        errors.push(`Line ${i + 2}: At least 2 options required`);
      }

      if (!options.some(o => o.isCorrect)) {
        errors.push(`Line ${i + 2}: At least one option must be correct`);
      }

      questions.push({
        text: questionText,
        type: questionType,
        explanation,
        options,
        isValid: errors.length === 0,
        errors
      });
    }

    this.importPreview = {
      totalRows: questions.length,
      validRows: questions.filter(q => q.isValid).length,
      invalidRows: questions.filter(q => !q.isValid).length,
      questions
    };
  }

  private parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current);
    return result;
  }

  async confirmImport(): Promise<void> {
    if (!this.importPreview || this.importPreview.validRows === 0) return;

    this.importing = true;

    try {
      if (this.isEditMode) {
        // Use API for existing tests
        const result = await this.safetyTestService.importQuestions(
          this.testId!,
          this.importCsvData,
          this.importReplaceMode
        );

        if (result) {
          const msg = this.importReplaceMode
            ? `Replaced existing questions with ${result.imported} imported questions`
            : `Imported ${result.imported} questions`;
          this.successMessage = msg;
          await this.loadTestDetail(); // Reload to get updated questions
        }
      } else {
        // For new tests, handle replace mode locally
        const validQuestions = this.importPreview.questions.filter(q => q.isValid);

        if (this.importReplaceMode) {
          this.questions = [];
        }

        for (const q of validQuestions) {
          this.questions.push({
            text: q.text,
            explanation: q.explanation || '',
            type: q.type as 'multiple_choice' | 'true_false',
            options: q.options,
            expanded: false
          });
        }

        const msg = this.importReplaceMode
          ? `Replaced existing questions with ${validQuestions.length} imported questions`
          : `Added ${validQuestions.length} questions`;
        this.successMessage = msg;
      }

      if (this.importModal) {
        this.importModal.hide();
      }
    } catch (error: any) {
      console.error('Failed to import questions:', error);
      this.errorMessage = 'Failed to import questions';
    } finally {
      this.importing = false;
    }
  }

  downloadCsvTemplate(): void {
    const csvContent = `Question Number,Question Text,Question Type,Explanation,Option A,Option A Correct,Option B,Option B Correct,Option C,Option C Correct,Option D,Option D Correct
1,What is the maximum altitude for recreational drone flights?,multiple_choice,The FAA limits recreational drone flights to 400 feet above ground level.,400 feet,TRUE,800 feet,FALSE,1000 feet,FALSE,No limit,FALSE
2,Drones must always yield right-of-way to manned aircraft.,true_false,All drones must yield to manned aircraft as per FAA regulations.,True,TRUE,False,FALSE,,,,`;

    this.downloadCsv(csvContent, 'questions-template.csv');
  }

  exportQuestions(): void {
    if (this.questions.length === 0) return;

    const csvContent = this.buildQuestionsCsv(this.questions);
    const title = this.form.get('shortName')?.value || this.form.get('title')?.value || 'test';
    const safeName = title.replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase();
    const filename = `questions-${safeName}-${new Date().toISOString().split('T')[0]}.csv`;
    this.downloadCsv(csvContent, filename);
  }

  private buildQuestionsCsv(questions: QuestionForm[]): string {
    const header = [
      'Question Number',
      'Question Text',
      'Question Type',
      'Explanation',
      'Option A', 'Option A Correct',
      'Option B', 'Option B Correct',
      'Option C', 'Option C Correct',
      'Option D', 'Option D Correct',
      'Option E', 'Option E Correct',
      'Option F', 'Option F Correct'
    ].join(',');

    const rows = questions.map((q, index) => {
      const row: string[] = [
        (index + 1).toString(),
        this.escapeCsvField(q.text),
        q.type,
        this.escapeCsvField(q.explanation || '')
      ];

      for (let i = 0; i < 6; i++) {
        if (q.options[i]) {
          row.push(this.escapeCsvField(q.options[i].text));
          row.push(q.options[i].isCorrect ? 'TRUE' : 'FALSE');
        } else {
          row.push('');
          row.push('');
        }
      }

      return row.join(',');
    });

    return [header, ...rows].join('\n');
  }

  private escapeCsvField(field: string): string {
    if (!field) return '';
    if (field.includes(',') || field.includes('"') || field.includes('\n')) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  }

  private downloadCsv(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
